import { isNumber } from 'lodash'
import { rootReducer } from '@wellesley/store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  type User,
  type Post as PostType,
  type GetApiV1PostsApiResponse,
  type DeleteApiV1PostsByPostIdCommentsAndCommentIdApiArg,
} from '@wellesley/openapi'
import { VirtualizerOffsetProps } from 'hooks/useInfiniteScrollVirtualizer'

import isQuote from '../Feed/helpers/isQuote'
import isRepost from '../Feed/helpers/isRepost'

import findFeed from './helpers/findFeed'
import getReduxKey from './helpers/getReduxKey'
import isPublicFeeds from './helpers/isPublicFeeds'
import sortByPinnedParam from './helpers/sortByPinnedParam'
import sortRepliesByPinnedAt from './helpers/sortRepliesByPinnedAt'
import expandRepliesToLevel from './helpers/expandRepliesToLevel'
import findAllFeedsAndUpdate from './helpers/findAllFeedsAndUpdate'
import isRepliesPage, { REPLIES_KEY } from './helpers/isRepliesPage'
import {
  sortByCreatedDateAscOrder,
  sortByCreatedDateDescOrder,
} from './helpers/sortByCreatedDate'

export const FEED_KEY = 'feed'
export const COMMENTS_KEY = 'comments'
export const FEEDS_KEY = 'feeds'
export const QUOTES_KEY = 'quotes'
export const REPOSTS_KEY = 'reposts'
export const FOLLOWING_KEY = 'following'
export const BOOKMARKS_KEY = 'bookmarks'

export type Post = PostType & {
  isOpen?: boolean
  isHidden?: boolean
  pinnedAt?: string | null
}

export type FeedState = {
  items: Post[]
  offsetProps: VirtualizerOffsetProps
}

const initialFeedState: FeedState = {
  items: [],
  offsetProps: null,
}

type FeedsData = Record<string, FeedState>

export type FeedsSliceState = {
  deletedPosts: string[]
  data: FeedsData
}

const initialState: FeedsSliceState = { data: {}, deletedPosts: [] }

export const feedsSlice = createSlice({
  name: 'feeds',
  initialState,
  reducers: {
    setFeedOffset: (
      state: FeedsSliceState,
      action: PayloadAction<{
        key: string
        offsetProps: VirtualizerOffsetProps
      }>,
    ) => {
      const { key, offsetProps } = action.payload

      if (state.data[key]) {
        state.data[key].offsetProps = offsetProps
      }
    },
    addList: (
      state,
      action: PayloadAction<{
        key: string
        posts: GetApiV1PostsApiResponse
        isNewList?: boolean
      }>,
    ) => {
      const { key, posts, isNewList } = action.payload

      const isPostPage = key.startsWith(`${FEED_KEY}-`)

      if (!state.data[key] || isNewList || isPostPage) {
        state.data[key] = { ...initialFeedState }

        const newItems = isPostPage ? expandRepliesToLevel(posts, 3) : posts
        state.data[key].items = newItems
      } else {
        state.data[key].items.push(...posts)
      }
    },
    addPost: (state: FeedsSliceState, action: PayloadAction<Post>) => {
      const keys = Object.keys(state.data)

      const ownerId = action.payload.ownerId || action.payload.author.id
      keys.forEach((key) => {
        if (isPublicFeeds(key) && action.payload.privacy !== 'PUBLIC') return

        if (
          isPublicFeeds(key) ||
          key === getReduxKey(FEEDS_KEY, ownerId) ||
          key.startsWith(FOLLOWING_KEY)
        ) {
          const isPostExist = state.data[key].items.some(
            (p: Post) => p.id === action.payload.id,
          )
          if (isPostExist) return

          const pinnedPostIndex = state.data[key].items.findLastIndex(
            (p: Post) => p.pinned,
          )

          if (pinnedPostIndex === -1) {
            state.data[key].items = [action.payload, ...state.data[key].items]
          } else {
            state.data[key].items.splice(pinnedPostIndex + 1, 0, action.payload)
          }
        } else {
          const userKey = getReduxKey(FEEDS_KEY, ownerId)

          if (!state.data[userKey]?.items.length && key.startsWith('feed')) {
            state.data[userKey] = { ...initialFeedState }
            state.data[userKey].items = [action.payload]
          }
        }
      })
    },
    updatePost: (state: FeedsSliceState, action: PayloadAction<Post>) => {
      const keys = Object.keys(state.data)

      let updatedFeeds = 0

      keys.forEach((key) => {
        const updateFeed = (feed: Post) => {
          const feedToUpdate = action.payload
          const isComment = feedToUpdate?.path?.length > 0
          const isTextUpdated = feed.text !== feedToUpdate.text

          const mappedPayload: Post = {
            ...feed,
            ...action.payload,
            ...(!action.payload.card && { card: null }),
            ...(!action.payload.address && { address: null }),
            ...(!feedToUpdate.settings?.sensitive && { summary: '' }),
            ...(isComment && { originalPost: null, parent: null }),
            ...(isTextUpdated && { translations: null }), // remove old translations
            address: action.payload.address || null,
          }

          if (feed) {
            Object.assign(feed, mappedPayload)

            updatedFeeds = +1
          }
        }

        findAllFeedsAndUpdate(
          state.data[key].items,
          action.payload.id,
          updateFeed,
        )

        const newFeed = action.payload

        const canAddPost = updatedFeeds === 0 && !newFeed.pinned

        if (canAddPost) {
          if (
            isPublicFeeds(key) ||
            key.startsWith(FEEDS_KEY) ||
            key.startsWith(REPOSTS_KEY)
          ) {
            const newFeeds = [newFeed, ...state.data[key].items].sort(
              sortByCreatedDateDescOrder,
            )

            state.data[key].items = newFeeds
          } else if (isRepliesPage(key)) {
            const isExistReplies = state.data[key].items.length

            const reply = {
              ...newFeed.parent!,

              replies: [{ ...newFeed, parent: null }],
            }

            if (isExistReplies) {
              state.data[key].items = [reply, ...state.data[key].items]
            } else {
              state.data[key].items = [reply]
            }
          } else {
            // its mostly for replies when changed visibility we show comment again
            // if reply not exist we add it to parant replies list
            const parentFeed = findFeed(newFeed.path?.at(-1) || '', {
              id: 'root-feed',
              replies: state.data[key].items,
            } as Post)

            if (parentFeed) {
              parentFeed.replies = [newFeed, ...(parentFeed.replies || [])]
            }
          }
        }

        if (isPublicFeeds(key) && newFeed.privacy !== 'PUBLIC') {
          state.data[key].items = state.data[key].items.filter(
            (f: Post) => f.id !== newFeed.id,
          )
        }

        updatedFeeds = 0
      })
    },
    deletePost: (
      state,
      action: PayloadAction<{ postId: string; repostOf?: string }>, // repostOf only from WS
    ) => {
      const { postId, repostOf } = action.payload
      const keys = Object.keys(state.data)

      const deletedPostId = repostOf || postId

      // if local event already sent we don't need to delete it again
      if (repostOf && state.deletedPosts.includes(repostOf)) return

      keys.forEach((key) => {
        const isReplies = isRepliesPage(key)

        const deletedFeed = findFeed(postId, {
          id: 'root-feed',
          replies: state.data[key].items,
        } as Post)

        const repostOfFeed = findFeed(repostOf || '', {
          id: 'root-feed',
          replies: state.data[key].items,
        } as Post)

        if (deletedFeed && isQuote(deletedFeed)) {
          state.deletedPosts.push(deletedFeed.id)
        }

        if (isReplies) {
          state.data[key].items = state.data[key].items.filter(
            (f: Post) => f.replies?.[0].id !== postId,
          )
        }

        let ownQuotes = 0

        const feedRepostOf = deletedFeed?.repostOf || repostOf

        if (feedRepostOf) {
          const updateReposts = (feed: Post, currentFeed: Post) => {
            if (!repostOf && deletedFeed && isQuote(deletedFeed)) {
              if (
                deletedFeed.author.id === currentFeed?.author.id &&
                !currentFeed.originalPost
              ) {
                ownQuotes += 1
              }

              if (feed && isNumber(feed?.stats.quotes)) {
                if (ownQuotes === 1 && feed.stats.quotes === 1) {
                  feed.quoted = false
                }
                feed.stats.quotes -= 1
              }
            } else {
              if (isQuote(deletedFeed)) {
                if (isNumber(feed.stats.quotes)) {
                  feed.stats.quotes -= 1
                }
              } else if (isNumber(feed.stats.reposts) && isRepost(feed)) {
                feed.stats.reposts -= 1
                if (!feed.stats.reposts) feed.reposted = false
              }
            }
          }

          const entity = deletedFeed || repostOfFeed
          const isRepostPost =
            (entity?.originalPost && isRepost(entity?.originalPost)) ||
            (entity && isRepost(entity))

          state.data[key].items.forEach((post: Post) => {
            if (
              post.repostOf === deletedPostId &&
              !isRepostPost &&
              (isQuote(post) ? post.originalPost?.id === postId : true)
            ) {
              if (post.originalPost) {
                post.originalPost.deleted = true
                post.originalPost.text = ''
              }
            }
          })

          findAllFeedsAndUpdate(
            state.data[key].items,
            feedRepostOf,
            updateReposts,
          )
        }

        const newItems = state.data[key].items.filter(
          (f: Post) => f.id !== postId,
        )

        state.data[key].items = newItems
      })
    },
    likePost: (
      state,
      action: PayloadAction<{
        id: string
        count?: number
        isLiked: boolean
        isIncrement?: boolean
      }>,
    ) => {
      const keys = Object.keys(state.data)
      const { id, count, isLiked, isIncrement } = action.payload

      keys.forEach((key) => {
        const update = (feed: Post) => {
          const currentCount = feed.stats.likes || 0
          const newCount = isNumber(count)
            ? count
            : isIncrement
              ? currentCount + 1
              : currentCount - 1

          feed.liked = isLiked
          feed.stats.likes = newCount
          feed.updatedAt = new Date().toISOString()
        }
        findAllFeedsAndUpdate(state.data[key].items, id, update)
      })
    },
    repost: (state, action: PayloadAction<{ userId: string; post: Post }>) => {
      const { userId, post } = action.payload
      const keys = Object.keys(state.data)

      keys.forEach((key) => {
        const isPostExist = state.data[key].items.find(
          (p: Post) => p.originalPost?.id === post.id || p.id === post.id,
        )

        if (isPostExist) return

        const updateStats = (feed: Post, currentFeed: Post) => {
          const newQuotesCounter = Number(post.originalPost?.stats.quotes) || 0
          const newRepostCounter = Number(post.originalPost?.stats.reposts) || 0

          if (isQuote(post)) feed.stats.quotes = newQuotesCounter
          else feed.stats.reposts = newRepostCounter

          if (currentFeed?.author.id === userId && isQuote(post)) {
            feed.quoted = true
          } else {
            if (post.author.id === userId && isRepost(post.originalPost!)) {
              feed.reposted = true
            }
          }
        }

        findAllFeedsAndUpdate(
          state.data[key].items,
          post.repostOf as string,
          updateStats,
        )

        if (
          isPublicFeeds(key) ||
          key.startsWith(FOLLOWING_KEY) ||
          [
            getReduxKey(FEEDS_KEY, post.author.id),
            getReduxKey(REPOSTS_KEY, post.author.id),
          ].includes(key)
        ) {
          if (isQuote(post)) state.data[key].items.unshift(post)
        }
      })
    },
    addComment: (state: FeedsSliceState, action: PayloadAction<Post>) => {
      const keys = Object.keys(state.data)

      keys.forEach((key) => {
        const isReplies = isRepliesPage(key)
        const isReposts = key.startsWith(REPOSTS_KEY)
        const isFeedPage = key.startsWith(`${FEED_KEY}-`)
        const isCommentExist = findFeed(action.payload.id, {
          id: 'root-feed',
          replies: state.data[key].items,
        } as Post)

        if (isCommentExist) return

        const feedId =
          isReplies && !isReposts
            ? action.payload.conversationId || ''
            : action.payload.path.at(-1)!

        const feed = findFeed(feedId, {
          id: 'root-feed',
          replies: state.data[key].items,
        } as Post)

        if (isReplies) {
          const feed = findFeed(
            action.payload.conversationId || '',
            state.data[key].items[0],
          )
          if (feed) {
            state.data[key].items.unshift({
              ...feed,
              replies: [action.payload],
            })
          } else if (
            state.data[key].items.length === 0 &&
            action.payload.parent
          ) {
            const reply = { ...action.payload, parent: null }
            state.data[key].items = [
              { ...action.payload.parent, replies: [reply] },
            ]
          }
        } else {
          if (feed) {
            if (feed.replies) {
              feed.replies.push(action.payload)
              feed.updatedAt = new Date().toISOString()
            } else if (!feed?.replies) {
              feed.replies = [action.payload]
              feed.updatedAt = new Date().toISOString()
            }
          }
        }

        const update = (_feed: Post, currentFeed: Post) => {
          const canUpdate = isQuote(currentFeed)
            ? action.payload.conversationId === currentFeed?.id
            : true

          if (
            currentFeed &&
            canUpdate &&
            isNumber(currentFeed.stats.comments)
          ) {
            currentFeed.stats.comments += 1
          }
        }

        if (isReplies) return

        action.payload.path.forEach((id) => {
          if (isFeedPage) {
            const feed = findFeed(id, {
              id: 'root-feed',
              replies: state.data[key].items,
            } as Post)

            if (feed && isNumber(feed.stats.comments)) {
              feed.stats.comments += 1
              feed.updatedAt = new Date().toISOString()
            }
          } else {
            findAllFeedsAndUpdate(state.data[key].items, id, update, true)
          }
        })
      })
    },
    deleteComment: (
      state: FeedsSliceState,
      action: PayloadAction<
        Pick<
          DeleteApiV1PostsByPostIdCommentsAndCommentIdApiArg,
          'commentId'
        > & {
          skipCounterUpdate?: boolean
          isVisibilityChanged?: boolean
        }
      >,
    ) => {
      const { commentId, skipCounterUpdate } = action.payload

      const keys = Object.keys(state.data)

      if (state.deletedPosts.includes(commentId)) return

      state.deletedPosts.push(commentId)

      keys.forEach((key) => {
        const isReplies = isRepliesPage(key)

        const commentFeed = findFeed(commentId, {
          id: 'comment-feed',
          replies: state.data[key].items,
        } as Post)

        if (isReplies) {
          state.data[key].items = state.data[key].items.filter(
            (r: Post) => r.replies?.[0].id !== commentId,
          )
        } else if (commentFeed) {
          if (commentFeed.originalPost?.id === commentId) {
            // Found via originalPost (this is a quote of the comment being deleted)
            commentFeed.originalPost.text = ''
            commentFeed.originalPost.uploads = []
            commentFeed.originalPost.deleted = true
          } else if (commentFeed.parent?.id === commentId) {
            commentFeed.parent.text = ''
            commentFeed.parent.uploads = []
            commentFeed.parent.deleted = true
          } else {
            commentFeed.text = ''
            commentFeed.uploads = []
            commentFeed.deleted = true
          }
          commentFeed.updatedAt = new Date().toISOString()

          commentFeed.path.forEach((id) => {
            const subFeed = findFeed(id, {
              id: 'sub-feed',
              replies: state.data[key].items,
            } as Post)

            if (
              subFeed &&
              isNumber(subFeed.stats.comments) &&
              subFeed.stats.comments > 0 &&
              !skipCounterUpdate
            ) {
              subFeed.stats.comments -= 1
              subFeed.updatedAt = new Date().toISOString()
            }
          })
        }
      })
    },
    toggleNestedComments: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload

      const keys = Object.keys(state.data)

      keys.forEach((key) => {
        const comment = findFeed(
          id,
          {
            id: 'comment-feed',
            replies: state.data[key].items,
          } as Post,
          undefined,
          (feed) => !!feed?.replies?.length,
        ) as Post

        if (comment) {
          comment.isOpen = !comment.isOpen
        }
      })
    },
    openCommentInThread: (
      state,
      action: PayloadAction<{ commentId: string }>,
    ) => {
      const { commentId } = action.payload

      const keys = Object.keys(state.data)

      keys.forEach((key) => {
        const comment = findFeed(
          commentId,
          {
            id: 'comment-feed',
            replies: state.data[key].items,
          } as Post,
          true,
          (feed) => !!feed?.replies?.length,
        )

        if (comment) {
          comment.path.forEach((id) => {
            const subFeed = findFeed(
              id,
              {
                id: 'sub-feed',
                replies: state.data[key].items,
              } as Post,
              true,
              (feed) => !!feed?.replies?.length,
            ) as Post

            if (subFeed) {
              subFeed.isOpen = true
            }
          })
        }
      })
    },
    removePrivatePosts: (state, action: PayloadAction<User>) => {
      const keys = Object.keys(state.data)
      const { id: userId } = action.payload

      keys.forEach((key) => {
        const newPosts = state.data[key].items.filter((f: Post) =>
          f.privacy === 'PRIVATE' ? f.author.id !== userId : true,
        )
        state.data[key].items = newPosts
      })
    },
    hideUserPosts: (state, action: PayloadAction<string>) => {
      const keys = Object.keys(state.data)
      const userId = action.payload

      keys.forEach((key) => {
        const newPosts = state.data[key].items.map((f: Post) => ({
          ...f,
          isHidden: f.author.id === userId,
        }))
        state.data[key].items = newPosts
      })
    },
    showUserPosts: (state, action: PayloadAction<string>) => {
      const keys = Object.keys(state.data)
      const userId = action.payload

      keys.forEach((key) => {
        const newPosts = state.data[key].items.map((f: Post) => ({
          ...f,
          isHidden: f.isHidden && f.author.id === userId ? false : f.isHidden,
        }))
        state.data[key].items = newPosts
      })
    },
    clearList: (state, action: PayloadAction<string>) => {
      const key = action.payload

      if (state.data[key]) {
        state.data[key].items = []
      }
    },
    clearAllLists: (state) => {
      const keys = Object.keys(state.data)

      keys.forEach((key) => {
        state.data[key].items = []
      })
    },
    updatePinPost: (
      state,
      action: PayloadAction<{
        postId: string
        pinned: boolean
        isComment?: boolean
      }>,
    ) => {
      const keys = Object.keys(state.data)
      const { postId, pinned, isComment } = action.payload

      keys.forEach((key) => {
        if (
          key.startsWith(FEEDS_KEY) ||
          key.startsWith(FEED_KEY) ||
          key.startsWith(REPOSTS_KEY)
        ) {
          const feed = findFeed(
            postId,
            {
              id: 'root-feed',
              replies: state.data[key].items,
            } as Post,
            true,
            isComment ? (feed: Post) => !feed?.isHighlightCopy : undefined,
          ) as Post

          if (feed) {
            feed.pinned = pinned
            feed.pinnedAt = pinned ? new Date().toISOString() : undefined
          }

          if (isComment) {
            const topLevelFeed = findFeed(
              feed?.conversationId as string,
              {
                id: 'root-feed',
                replies: state.data[key].items,
              } as Post,
            )

            if (topLevelFeed) {
              if (pinned) {
                const feedWithoutReplies = {
                  ...feed,
                  isOpen: false,
                  isHighlightCopy: true,
                  replies: [],
                } as Post

                const topReplies = topLevelFeed.replies || []

                if (feed && feedWithoutReplies.path.length > 1) {
                  topReplies.unshift(feedWithoutReplies)
                }
                topLevelFeed.replies = sortRepliesByPinnedAt(topReplies)
              } else {
                const isTopLevelFeed =
                  feed?.path?.length && feed.path.length === 1

                if (isTopLevelFeed) {
                  topLevelFeed.replies = sortRepliesByPinnedAt(
                    topLevelFeed.replies?.sort(sortByCreatedDateAscOrder) || [],
                  )
                } else {
                  topLevelFeed.replies =
                    topLevelFeed.replies?.filter((f) => f?.id !== feed?.id) ||
                    []
                }
              }
            }
          } else {
            if (pinned) {
              const newItems = state.data[key].items.filter(
                (f: Post) => f.id !== postId,
              )
              if (feed) {
                state.data[key].items = [feed, ...newItems]
              }
            } else {
              state.data[key].items = state.data[key].items
                .sort(sortByCreatedDateDescOrder)
                .sort(sortByPinnedParam)
            }
          }
        }
      })
    },
    deleteRepost: (
      state,
      action: PayloadAction<{ postId: string; userId: string }>,
    ) => {
      const keys = Object.keys(state.data)
      const { postId } = action.payload

      state.deletedPosts.push(postId)

      keys.forEach((key) => {
        if (key.startsWith(REPLIES_KEY)) {
          const update = (feed: Post) => {
            const reply = feed.replies?.[0]
            if (reply) {
              reply.reposted = false
              if (isNumber(reply.stats.reposts)) {
                reply.stats.reposts -= 1
              }
            }
          }
          findAllFeedsAndUpdate(state.data[key].items, postId, update)
        } else if (key.startsWith(REPOSTS_KEY)) {
          const update = (feed: Post) => {
            const originalFeed = feed.originalPost
            if (originalFeed) {
              originalFeed.reposted = false
              if (isNumber(originalFeed.stats.reposts)) {
                originalFeed.stats.reposts -= 1
              }
            }
          }
          findAllFeedsAndUpdate(state.data[key].items, postId, update)
        } else {
          const update = (feed: Post) => {
            feed.reposted = false
            if (isNumber(feed.stats.reposts)) {
              feed.stats.reposts -= 1
            }
          }
          findAllFeedsAndUpdate(state.data[key].items, postId, update)
        }
      })
    },
    saveMoreComments: (
      state,
      action: PayloadAction<{ postId: string; comments: Post[] }>,
    ) => {
      const keys = Object.keys(state.data)
      const { postId, comments } = action.payload

      keys.forEach((key) => {
        if (key.startsWith(FEED_KEY)) {
          const feed = findFeed(postId, {
            id: 'root-feed',
            replies: state.data[key].items,
          } as Post)

          if (feed) {
            feed.replies?.push(...comments)
          }
        }
      })
    },
  },
  selectors: {
    selectFeedState: (state: FeedsSliceState, key: string) =>
      state.data[key]
        ? {
            ...state.data[key],
            items: state.data[key].items.filter((f) => !f.isHidden),
          }
        : { ...initialFeedState },
  },
}).injectInto(rootReducer)
